import React, { ClipboardEventHandler, FormEvent, useCallback, useContext, useRef } from 'react';
import { PostEditorContext } from '../states/PostEditorProvider';
import { PostEditorGroupDataContext } from '../states/PostEditorGroupDataProvider';
import { ActiveCustomizeIndexContext } from '../states/ActiveCustomizeIndexProvider';
import { TitleBlockView, TitleBlockViewProps } from './TitleBlock.view';
import { GroupPostAttributesType } from '../../../models/post-editor';
import { getSelectedProfilesWithBoards } from '../helpers/getSelectedProfilesWithBoards';
import { mediaContainsVideo } from '../helpers/mediaContainsVideo';
import DOMPurify from 'dompurify';
import { hasSeverity } from '../validators/helpers/severity-checker';
import { ErrorMessageSeverity } from '../../../constants/form';
import { getCustomizationProfileId } from '../validators/helpers/customization-getter';
import { PostEditorErrorKeys } from '../../../models/validator-props';
import { getErrorMessages } from '../validators/helpers/getErrorMessages';
import { PostStatus } from '../../../models/graphql';
import { formatErrorMessages } from '../validators/helpers/formatErrorMessages';
import { Tooltip } from '../../../components/actions/Tooltip';

const MAX_LENGTH = 100;
const errorsList: PostEditorErrorKeys[] = ['titleCharLimit'];

const getCursorPosition = (node: HTMLDivElement) => {
  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(node);
    preCaretRange.setEnd(range.startContainer, range.startOffset);
    return preCaretRange.toString().length; // Characters before cursor
  }
  return 0;
};

const setCursorPosition = (node: HTMLDivElement, position: number) => {
  const range = document.createRange();
  const selection = window.getSelection();

  let charCount = 0;
  let found = false;

  const traverseNodes = (nodes: NodeListOf<ChildNode>) => {
    const itterbaleNodes = Array.from(nodes);
    for (const node of itterbaleNodes) {
      if (found) break;

      if (node.textContent && node.nodeType === Node.TEXT_NODE) {
        const nextCharCount = charCount + node.textContent.length;
        if (position <= nextCharCount) {
          range.setStart(node, position - charCount);
          found = true;
          break;
        }
        charCount = nextCharCount;
      } else {
        traverseNodes(node.childNodes);
      }
    }
  };

  traverseNodes(node.childNodes);

  if (selection) {
    selection.removeAllRanges();
    selection.addRange(range);
  }
};

export const TitleBlockController = (): JSX.Element => {
  const inputRef = useRef<HTMLDivElement>(null);
  const { state, dispatch, profiles: socialProfiles } = useContext(PostEditorContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const postGroup = state.postGroups[postGroupIndex];
  const { customizeContentPerProfile, posts, errors } = postGroup;
  const title =
    customizeContentPerProfile && typeof customizeIndex === 'number'
      ? posts[customizeIndex].content.title
      : postGroup.groupPostAttributes.content.title;
  const media =
    customizeContentPerProfile && typeof customizeIndex === 'number'
      ? posts[customizeIndex].content.media
      : postGroup.groupPostAttributes.content.media;
  const selectedProfiles = getSelectedProfilesWithBoards(
    postGroup.profiles,
    socialProfiles,
    customizeIndex,
    customizeContentPerProfile,
    posts
  );
  const customizedProfileId = getCustomizationProfileId(postGroup, customizeIndex);
  const isPostDraft =
    customizeContentPerProfile && typeof customizeIndex === 'number'
      ? posts[customizeIndex].postStatus === PostStatus.DRAFT
      : postGroup.groupPostAttributes.postStatus === PostStatus.DRAFT;

  const hasError =
    !isPostDraft && hasSeverity(errors, ErrorMessageSeverity.error, customizedProfileId, undefined, errorsList);
  const errorMessagesArray = getErrorMessages(errors, customizedProfileId, undefined, errorsList);
  const errorMessages: TitleBlockViewProps['errorMessages'] = {
    severity: hasError ? ErrorMessageSeverity.error : null,
    messages:
      errorMessagesArray.length && hasError ? (
        <Tooltip
          icon={'alert'}
          target={`#tooltipTargetTitle_${customizeIndex !== undefined ? customizeIndex : 'group'}_${postGroupIndex}`}
          position={'right'}
          pointer={true}
          type={'inlined'}
          autoZIndex={false}
          style={{ zIndex: 2000 }}>
          {formatErrorMessages(errorMessagesArray)}
        </Tooltip>
      ) : (
        <></>
      )
  };

  const isHTMLDivElementGuard = (value: EventTarget): value is HTMLDivElement => {
    return 'outerText' in value;
  };

  const handlePaste: ClipboardEventHandler<HTMLDivElement> = e => {
    e.preventDefault();
    if (!inputRef.current) return;

    const pastedText = e.clipboardData.getData('text/plain');
    const sanitizedText = DOMPurify.sanitize(pastedText);

    if (pastedText && sanitizedText) {
      // Insert the plain text manually
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents(); // Remove selected content if any
        range.insertNode(document.createTextNode(sanitizedText));
        selection.collapseToEnd(); // Move cursor to the end of selection
      }

      // we dispatch an event in order to trigger update since input changed
      inputRef.current.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
    }
  };

  const updateAction = useCallback(
    (event: FormEvent<HTMLDivElement>, current: GroupPostAttributesType['content']['title']) => {
      if (!isHTMLDivElementGuard(event.target)) throw new Error('event.target is not a valid Div element');

      if (!inputRef.current) return;

      const cursorPosition = getCursorPosition(inputRef.current); // Save cursor position
      const currentText = event.target.innerText;
      const noLineBreaks = currentText.replace(/[\r\n]+/g, '').trim();
      const sanitizedText = noLineBreaks === '' ? noLineBreaks : DOMPurify.sanitize(currentText);

      if (current !== sanitizedText) {
        // Ensure the field isn't empty to preserve the cursor
        if (sanitizedText === '') {
          event.target.innerText = '\u200B'; // Add a zero-width space to allow setting cursor range
          setCursorPosition(inputRef.current, 0); // Place cursor at the start
        } else {
          event.target.innerText = sanitizedText.replace(/\u200B/, '');
          // Restore cursor position if needed
          setCursorPosition(inputRef.current, cursorPosition); // Restore cursor position
        }
        dispatch({
          type: 'titleUpdate',
          payload: {
            customizeIndex,
            postGroupIndex,
            title: sanitizedText ?? null
          }
        });
      }
    },
    [dispatch, customizeIndex, postGroupIndex]
  );

  const showTitleBlock = selectedProfiles.reduce((prev, curr) => {
    if (prev) return prev;
    switch (curr.__typename) {
      case 'PinterestSocialProfile':
        return true;
      case 'FacebookSocialProfile':
      case 'TwitterSocialProfile':
      case 'LinkedInSocialProfile':
        return mediaContainsVideo(media);
      default:
        return false;
    }
  }, false);

  return showTitleBlock ? (
    <TitleBlockView
      ref={inputRef}
      title={title}
      updateAction={updateAction}
      maxLength={MAX_LENGTH}
      errorMessages={errorMessages}
      handlePaste={handlePaste}
    />
  ) : (
    <></>
  );
};
